<script lang="ts" setup>
// Shell mobile v2 plein écran (port de MobileApp) : head compact (menu, dossier, notifications),
// tags + équipe, zone scrollable, bottom tab bar. Monté à la place du chrome desktop au format
// mobile, une fois la feature `mobile` ouverte (cf. layouts/default.vue, useMobileShell).
//
// C'est un CHROME, pas un routeur : la zone scrollable rend `<slot />` (la page routée), et la
// tab bar navigue réellement. Un onglet piloté par un état local afficherait un écran sans
// rapport avec l'URL et rendrait les pages non portées inatteignables.
//
// Chrome branché sur les vraies données : dossier (useOpportunityDetail), score Analyse
// (useAnalyseScore), notifications (useUnreadNotifications), onglets (useOpportunityTabs),
// intervenants = utilisateur courant (comme le Collabs desktop — pas de source réelle).
import { storeToRefs } from 'pinia';
import { useOpportunityDetail } from '~/composables/opportunities/useOpportunityDetail';
import { getOpportunityStatusLabel } from '~/utils/opportunityStatus';
import type { OpportunityTab } from '~/composables/useOpportunityTabs';
import { isBrowserHandledClick } from '~/utils/popupNavigation';

const { opportunity } = useOpportunityDetail();
const usersStore = useUsersStore();
const { authUserData } = storeToRefs(usersStore);

const dossierOpen = ref(false);
const navOpen = ref(false);
const notifOpen = ref(false);
const teamOpen = ref(false);
const scrollRef = ref<HTMLElement | null>(null);

// Thème partagé avec la topbar desktop (useDarkMode pose `data-theme` sur <html>). Auparavant un
// ref local : la bascule mobile ne touchait jamais le document, donc le shell restait en clair.
// Le contenu legacy rendu dans le `<slot />`, lui, ne suit délibérément pas le thème (cf.
// useDarkMode).
const { isDark: dark, toggle: toggleTheme } = useDarkMode();

// --- Dossier courant (réel) ---
const reqId = computed<string>(() => String(opportunity.value?.reqId ?? ''));
const activeClient = computed<string>(() => {
  const persons = [...(opportunity.value?.persons ?? [])]
    .sort((a, b) => (b.main === true ? 1 : 0) - (a.main === true ? 1 : 0));
  const names = persons.map(p => p.lastName).filter(Boolean);
  return names.length > 1 ? `${names[0]} - ${names[1]}` : (names[0] ?? '');
});
const dossiers = computed(() => [{ id: reqId.value, activity: 'RAC' as const, client: activeClient.value }]);
const activeDossier = computed<string>(() => reqId.value);

const statusLabel = computed<string>(() => getOpportunityStatusLabel(opportunity.value?.status));

// --- Score Analyse (réel, partagé avec les subtabs desktop via useAnalyseScore) ---
const { scorePercent: score, hasScore, severity: scoreSeverity } = useAnalyseScore();
const scoreTone = computed<'ok' | 'orange' | 'gray'>(() =>
  scoreSeverity.value === 'success' ? 'ok' : scoreSeverity.value === 'warn' ? 'orange' : 'gray');

// --- Intervenants = utilisateur courant (réel) ---
const team = computed(() => {
  const user = authUserData.value;
  if (!user) { return []; }
  return [{ initials: (user.username ?? '').slice(0, 2).toUpperCase() || '-', name: user.username ?? '', role: user.position?.slug ?? '', tone: 'a' }];
});

// --- Notifications (réelles) — fetch + marquage lu partagés avec la cloche desktop (NotifBell). ---
const { items: notifs, fetchNotifs, markRead: markNotifRead } = useUnreadNotifications(20);
const bell = computed<number>(() => notifs.value.length);

const { screenV2 } = useScreenVersion();
const { bpClasses } = useMobileShell();

// --- Onglets dossier : config + navigation partagées avec le SubTabs desktop (useOpportunityTabs).
// L'onglet actif est déduit de la route, jamais d'un état local (deep link cohérent avec le slot).
const { tabs, inDossier, isActive: isActiveTab, tabHref, openTab } = useOpportunityTabs();
function onTabClick (t: OpportunityTab, event: MouseEvent): void {
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  openTab(t);
  // Reset du scroll au changement de page (spécifique mobile).
  if (t.kind === 'route' && scrollRef.value) { scrollRef.value.scrollTop = 0; }
}

onMounted(() => {
  void fetchNotifs();
});
</script>

<template>
  <div
    class="gm-phone gm-fullscreen"
    :data-theme="dark ? 'dark' : undefined"
  >
    <div class="gm-head">
      <div class="gm-head-top">
        <button
          class="gm-head-btn"
          title="Menu"
          @click="navOpen = true"
        >
          <DsIcon
            name="bars"
            :size="18"
          />
        </button>
        <button
          v-if="inDossier"
          class="gm-dossier"
          type="button"
          @click="dossierOpen = true"
        >
          <span class="gm-dossier-ref">RAC #{{ activeDossier }}</span>
          <span class="gm-dossier-name">{{ activeClient }}</span>
          <span class="gm-caret" />
        </button>
        <span
          v-else
          class="gm-dossier-name"
        >GoldoRAC</span>
        <button
          class="gm-head-btn"
          title="Notifications"
          @click="notifOpen = true"
        >
          <DsIcon
            name="bell"
            :size="17"
          />
          <span
            v-if="bell > 0"
            class="gm-dot"
          />
        </button>
      </div>
      <div
        v-if="inDossier"
        class="gm-head-sub"
      >
        <span
          v-if="statusLabel"
          class="gm-tag info"
        >{{ statusLabel }}</span>
        <span
          v-if="hasScore"
          :class="['gm-tag', scoreTone === 'ok' ? 'ok' : scoreTone === 'orange' ? 'orange' : 'neutral']"
        >Note {{ score }}</span>
        <button
          v-if="team.length"
          class="gm-team"
          title="Intervenants sur le dossier"
          @click="teamOpen = true"
        >
          <span
            v-for="c in team.slice(0, 3)"
            :key="c.initials"
            :class="['gm-team-av', `tone-${c.tone}`]"
          >{{ c.initials }}</span>
          <span
            v-if="team.length > 3"
            class="gm-team-av more"
          >+{{ team.length - 3 }}</span>
        </button>
      </div>
    </div>

    <div
      ref="scrollRef"
      :class="['gm-scroll', ...bpClasses, { 'gm-scroll--legacy': !screenV2 }]"
    >
      <slot />
    </div>

    <div
      v-if="inDossier"
      class="gm-tabbar"
    >
      <a
        v-for="t in tabs"
        :key="t.key"
        :href="tabHref(t)"
        :class="['gm-tab', { active: isActiveTab(t) }]"
        @click="onTabClick(t, $event)"
      >
        <span class="gm-tab-ico">
          <DsIcon
            :name="t.icon"
            :size="21"
          />
          <span
            v-if="t.key === 'analysis' && hasScore"
            :class="['gm-tab-badge', scoreTone]"
          >{{ score }}</span>
        </span>
        <span class="gm-tab-lbl">{{ t.shortLabel }}</span>
      </a>
    </div>

    <MobileDossierSheet
      v-if="dossierOpen"
      :dossiers="dossiers"
      :active="activeDossier"
      @close="dossierOpen = false"
    />
    <MobileNavDrawer
      v-if="navOpen"
      :theme="dark ? 'dark' : 'light'"
      @close="navOpen = false"
      @toggle-theme="toggleTheme"
    />
    <MobileNotifSheet
      v-if="notifOpen"
      :notifs="notifs"
      @read="markNotifRead"
      @close="notifOpen = false"
    />
    <MobileTeamSheet
      v-if="teamOpen"
      :team="team"
      @close="teamOpen = false"
    />
  </div>
</template>
